import React, { useRef } from 'react'
import type { TextareaFieldProps } from './Textarea.types'

export default function TextareaField({
  name,
  value,
  label,
  placeholder,
  rows = 8,
  required,
  disabled,
  error,
  helperText,
  clearable = true,
  onChange,
  onClear,
}: TextareaFieldProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const hasValue = value !== undefined && value !== ''

  const rootClasses = [
    'rd-textarea',
    'rd-textarea--medium',
    'rd-textarea--floating',
    error ? 'rd-textarea--error' : '',
    disabled ? 'rd-textarea--disabled' : '',
    hasValue ? 'has-value' : '',
  ].filter(Boolean).join(' ')

  const labelClasses = [
    'rd-textarea__label',
    required ? 'rd-textarea__label--required' : '',
  ].filter(Boolean).join(' ')

  const handleClear = () => {
    if (onClear) {
      onClear()
    } else if (onChange) {
      const nativeTextAreaValueSetter = Object.getOwnPropertyDescriptor(
        window.HTMLTextAreaElement.prototype, 'value',
      )?.set
      if (nativeTextAreaValueSetter && textareaRef.current) {
        nativeTextAreaValueSetter.call(textareaRef.current, '')
        const event = new Event('input', { bubbles: true })
        textareaRef.current.dispatchEvent(event)
      }
    }
  }

  return (
    <div className={rootClasses}>
      <div className="rd-textarea__wrapper">
        <textarea
          ref={textareaRef}
          className="rd-textarea__field"
          name={name}
          value={value}
          placeholder={placeholder || ' '}
          rows={rows}
          required={required}
          disabled={disabled}
          onChange={onChange}
        />
        {label && <label className={labelClasses}>{label}</label>}
        {clearable && hasValue && !disabled && (
          <button
            type="button"
            className="rd-textarea__clear is-visible"
            onClick={handleClear}
            tabIndex={-1}
            aria-label="Clear"
          >
            <i className="rd-icon-cross" />
          </button>
        )}
      </div>
      {error && <span className="rd-textarea__error-text">{error}</span>}
      {helperText && !error && <span className="rd-textarea__helper-text">{helperText}</span>}
    </div>
  )
}
